import { useNavigate } from '@tanstack/react-router';
import type { Project } from '@hangar/shared';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';

interface SwitcherProps {
  projects: Project[] | undefined;
  current: Project | undefined;
  rail: boolean;
}

function LoadingLabel({ rail }: { rail: boolean }) {
  return (
    <>
      <Skeleton className="size-2 rounded-full" />
      {!rail ? <Skeleton className="h-3.5 w-16" /> : null}
    </>
  );
}

function CurrentLabel({ current, rail }: { current: Project | undefined; rail: boolean }) {
  return (
    <>
      <span aria-hidden className={cn('size-2 shrink-0 rounded-full', current ? 'bg-succeeded' : 'bg-subtle')} />
      {!rail ? (
        <>
          <span className="min-w-0 flex-1 truncate text-left font-medium">{current?.name ?? 'No project yet'}</span>
          <Icon name="expand_more" size={18} className="text-subtle" />
        </>
      ) : null}
    </>
  );
}

function ProjectItems({ projects, current }: { projects: Project[] | undefined; current: Project | undefined }) {
  const navigate = useNavigate();
  if (!projects || projects.length === 0) return <DropdownMenuItem disabled>No projects yet</DropdownMenuItem>;
  return (
    <>
      {projects.map((p) => (
        <DropdownMenuItem key={p.id} onSelect={() => void navigate({ to: '/projects/$slug', params: { slug: p.slug } })}>
          <span className="min-w-0 flex-1 truncate">
            {p.name} <span className="ml-1 font-mono text-xs text-subtle">{p.slug}</span>
          </span>
          {p.id === current?.id ? <Icon name="check_circle" size={16} fill className="text-primary" /> : null}
        </DropdownMenuItem>
      ))}
    </>
  );
}

export function ProjectSwitcher({ projects, current, rail }: SwitcherProps) {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={cn('h-10 w-full font-normal', rail ? 'justify-center px-0' : 'justify-start gap-2.5 px-2.5')} title={current ? `Project: ${current.name}` : 'Project'}>
          {projects === undefined ? <LoadingLabel rail={rail} /> : <CurrentLabel current={current} rail={rail} />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent aria-label="Switch project" align="start" side={rail ? 'right' : 'bottom'} sideOffset={rail ? 8 : 4} className="w-64">
        <ProjectItems projects={projects} current={current} />
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void navigate({ to: '/', hash: 'projects' })}>
          <Icon name="add" size={18} className="text-muted-foreground" />
          New project
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
